export const TWO_DECIMAL_FIELDS = new Set([
  "perKmRate",
  "extraKmRate",
  "driverCharge",
  "waitingCharge",
  "nightCharge",
  "tollCharge",
  "commissionPercent",
  "airportSurcharge",
  "price",
  "extraHourRate",
  "premiumSurcharge",
  "discount",
  "amount",
]);

export const MAX_CURRENCY_VALUE = 9999999;

export const FIELD_LIMITS = {
  perKmRate: { min: 0, max: MAX_CURRENCY_VALUE, label: "Per kilometre rate" },
  extraKmRate: { min: 0, max: MAX_CURRENCY_VALUE, label: "Extra kilometre rate" },
  driverCharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Driver charge" },
  waitingCharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Waiting charge" },
  nightCharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Night charge" },
  tollCharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Toll charge" },
  airportSurcharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Airport surcharge" },
  price: { min: 0, max: MAX_CURRENCY_VALUE, label: "Price" },
  extraHourRate: { min: 0, max: MAX_CURRENCY_VALUE, label: "Extra hour rate" },
  premiumSurcharge: { min: 0, max: MAX_CURRENCY_VALUE, label: "Premium amount" },
  amount: { min: 0, max: MAX_CURRENCY_VALUE, label: "Surcharge amount" },
  commissionPercent: { min: 0, max: 100, label: "Commission" },
  discount: { min: 0, max: 100, label: "Discount" },
};

export const getProgressiveDecimalInput = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return { value: fallback, isValid: true };
  }

  const raw = String(value).trim();

  if (raw === "") {
    return { value: "", isValid: true };
  }

  const progressivePattern = /^\d*(\.\d{0,2})?$/;
  if (!progressivePattern.test(raw)) {
    return { value: fallback, isValid: false };
  }

  return { value: raw, isValid: true };
};

export const clampToTwoDecimals = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) {
    return { value: fallback, isValid: true };
  }

  const raw = String(value).trim();

  if (/e/i.test(raw)) {
    return { value: fallback, isValid: false };
  }

  const normalized = raw.startsWith(".") ? `0${raw}` : raw;
  const validDecimalPattern = /^(?:\d+|\d*\.\d{1,2})$/;
  if (!validDecimalPattern.test(normalized)) {
    return { value: fallback, isValid: false };
  }

  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return { value: fallback, isValid: false };
  }

  return {
    value: Math.round((parsed + Number.EPSILON) * 100) / 100,
    isValid: true,
  };
};

export const parseNumericInput = (fieldName, value, fallback = 0) => {
  if (TWO_DECIMAL_FIELDS.has(fieldName)) {
    return clampToTwoDecimals(value, fallback).value;
  }

  if (value === "" || value === null || value === undefined) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export const validateNumericRange = (fieldName, value) => {
  const limits = FIELD_LIMITS[fieldName];

  if (!limits || value === "" || value === null || value === undefined) {
    return { isValid: true, message: "" };
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return { isValid: false, message: "Enter a valid number." };
  }

  if (numericValue < limits.min || numericValue > limits.max) {
    if (limits.max === 100) {
      return { isValid: false, message: `${limits.label} must be between 0 and 100.` };
    }

    return {
      isValid: false,
      message: `${limits.label} must be between 0 and ₹${limits.max.toLocaleString("en-IN")}.`,
    };
  }

  return { isValid: true, message: "" };
};

export const getNumericInputProps = (fieldName) => {
  if (TWO_DECIMAL_FIELDS.has(fieldName)) {
    const limits = FIELD_LIMITS[fieldName];
    return {
      min: limits?.min ?? 0,
      max: limits?.max,
      step: 0.01,
      inputmode: "decimal",
    };
  }

  return { min: 0, step: 1 };
};
